import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button } from '@mui/material';
import { GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import {
  networkCreateGateway,
  networkUpdateGateway,
  useFetchGatewayActiveTypeList,
  useFetchGatewayPriceTypeList,
  useFetchGatewayTypeList,
} from 'src/network/gateway';
import { generateSelectMenuItems } from 'src/utils';
import { GATEWAY_LIST } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

const GatewayForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const [gatewayTypes, setGatewayTypes] = useState([]);
  const [selectedGatewayType, setSelectedGatewayType] = useState('');

  const [gatewayActiveTypes, setGatewayActiveTypes] = useState([]);
  const [selectedGatewayActiveType, setSelectedGatewayActiveType] = useState('');

  const [gatewayPriceTypes, setGatewayPriceTypes] = useState([]);
  const [selectedGatewayPriceType, setSelectedGatewayPriceType] = useState('');

  const gatewayDetails = modal?.data;
  const isEditMode = !!gatewayDetails;

  const defaultValues = getDefaultValues(gatewayDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GatewayGatewayActiveTypeID: selectedGatewayActiveType,
      GatewayGatewayTypeID: selectedGatewayType,
      GatewayGatewayPriceTypeID: selectedGatewayPriceType,
      GatewayCredit: +values.GatewayCredit,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGateway(gatewayDetails?.GatewayID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('درگاه پرداخت موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [GATEWAY_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGateway(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('درگاه پرداخت موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [GATEWAY_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // --------------------------------------------------------------------

  const { data: gatewayTypeResult } = useFetchGatewayTypeList();
  const { data: gatewayActiveTypeResult } = useFetchGatewayActiveTypeList();
  const { data: gatewayPriceTypeResult } = useFetchGatewayPriceTypeList();

  useEffect(() => {
    if (gatewayTypeResult) {
      const result = generateSelectMenuItems(gatewayTypeResult?.data?.items, 'GatewayTypeLabel', 'GatewayTypeID');
      setGatewayTypes(result);
      setSelectedGatewayType(gatewayDetails?.GatewayTypeID || result?.[0]?.id);
    }
  }, [gatewayTypeResult]);

  useEffect(() => {
    if (gatewayActiveTypeResult) {
      const result = generateSelectMenuItems(gatewayActiveTypeResult?.data?.items, 'GatewayActiveTypeLabel', 'GatewayActiveTypeID');
      setGatewayActiveTypes(result);
      setSelectedGatewayActiveType(gatewayDetails?.GatewayActiveTypeID || result?.[0]?.id);
    }
  }, [gatewayActiveTypeResult]);

  useEffect(() => {
    if (gatewayPriceTypeResult) {
      const result = generateSelectMenuItems(gatewayPriceTypeResult?.data?.items, 'GatewayPriceTypeLabel', 'GatewayPriceTypeID');
      setGatewayPriceTypes(result);
      setSelectedGatewayPriceType(gatewayDetails?.GatewayPriceTypeID || result?.[0]?.id);
    }
  }, [gatewayPriceTypeResult]);

  // --------------------------------------------------------------------

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="GatewayLabel" type="text" label="عنوان" />

              <GeneralUpload
                label="لوگو"
                id="gateway-logo"
                name="GatewayLogo"
                afterChangeAction={(res) => {
                  setValue('GatewayLogo', res);
                  trigger('GatewayLogo');
                }}
              />

              <RHFTextField name="GatewayToken" type="text" label="توکن درگاه" />

              <RHFTextField name="GatewayClientId" type="text" label="شناسه کاربری" />

              <RHFTextField name="GatewayClientToken" type="text" label="توکن کاربری" />

              <RHFTextField name="GatewayCredit" type="text" label="اعتبار درگاه" />

              <GeneralSelectMenu
                label="وضعیت درگاه"
                name="GatewayGatewayActiveTypeID"
                options={gatewayActiveTypes}
                value={selectedGatewayActiveType}
                onChangeValue={setSelectedGatewayActiveType}
              />

              <GeneralSelectMenu
                label="نوع درگاه"
                name="GatewayGatewayTypeID"
                options={gatewayTypes}
                value={selectedGatewayType}
                onChangeValue={setSelectedGatewayType}
              />

              <GeneralSelectMenu
                label="واحد پرداخت درگاه"
                name="GatewayGatewayPriceTypeID"
                options={gatewayPriceTypes}
                value={selectedGatewayPriceType}
                onChangeValue={setSelectedGatewayPriceType}
              />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GatewayForm;
